// data/PillarPageData.ts
// Centralised data for FAQ Schema (15 items) and HowTo Schema (12 processes).
// Imported by BlogView.tsx and injected as JSON-LD only on the pillar page.

// ============================================================
// FAQ ITEMS — 15 questions
// ============================================================

export const PILLAR_FAQ = [
  {
    question: "What is agentic commerce infrastructure?",
    answer:
      "A universal intent gateway that routes natural language queries to execution via Shopify UCP and Stripe ACP protocols. It enables constraint-heavy discovery and autonomous transactions where AI agents find, evaluate, and purchase products on behalf of users.",
  },
  {
    question: "How does Shopify UCP integration work?",
    answer:
      "Shopify's Universal Commerce Protocol provides global catalog access through standardised business profiles. A UCP proxy acts as a stateless translation layer — agents send checkout operations (create, update, complete) which the proxy translates to native platform APIs like WooCommerce Store API or Wix API.",
  },
  {
    question: "What are the three execution paths?",
    answer:
      "Assisted (user reviews and completes on merchant site via UCP permalinks), Connect (one-click purchase with pre-filled details via Stripe Connect), and Autonomous (zero-click purchase via Session Payment Tokens for trust-verified users).",
  },
  {
    question: "What is constraint-heavy discovery?",
    answer:
      "Parsing natural language queries into structured constraints like age ranges, safety certifications, ingredient exclusions, or budget brackets — then matching those constraints against real merchant inventory rather than ranking by keywords.",
  },
  {
    question: "How does the trust ladder work?",
    answer:
      "Users start at Assisted (review everything), graduate to Connect (one-click) after building transaction history, and can opt into Autonomous (zero-click) with explicit consent and budget constraints. Users can downgrade at any time.",
  },
  {
    question: "What is a Session Payment Token (SPT)?",
    answer:
      "A temporary, scoped payment credential issued by Stripe ACP that lets an agent execute a transaction without the user re-authenticating. SPTs have built-in constraints: amount limits, merchant restrictions, and time windows.",
  },
  {
    question: "Who co-developed UCP?",
    answer:
      "UCP is an open standard co-developed by Google, Shopify, Etsy, Wayfair, Target, and Walmart. It is transport-agnostic and supports REST, JSON-RPC, AP2, A2A, and MCP protocols.",
  },
  {
    question: "How does multi-agent orchestration work?",
    answer:
      "A supervisor agent delegates tasks to specialised sub-agents (e.g. Shopping Assistant, Cart Manager) via an MCP gateway. Each agent is independently deployable, scales on demand, and isolates failures. The supervisor maintains conversation context and routes user intent to the right specialist.",
  },
  {
    question: "What vertical domains does the infrastructure support?",
    answer:
      "Any constraint-heavy domain. Toys (ToyFinder, live in production), wine, skincare, fashion, and travel are the initial verticals. The 80/20 architecture reuses 80% shared gateway logic and adds 20% vertical-specific constraint schemas.",
  },
  {
    question: "How does the payment security model work?",
    answer:
      "Card numbers are tokenised via Visa, only the last 4 digits are stored, CVV is never persisted, and encrypted tokens are used for all payments. FIDO2 passkeys bind tokens to specific devices, and 3D Secure escalation handles additional authentication when required.",
  },
  {
    question: "What happens when a checkout requires escalation?",
    answer:
      "The UCP proxy returns a continue_url where the buyer can finalise the transaction in a browser. Escalation triggers include 3D Secure authentication, age verification, legal disclaimers, or custom input fields.",
  },
  {
    question: "What infrastructure do merchants need?",
    answer:
      "Minimum: a product catalog via Shopify UCP (30-minute setup, no code). Optimal: UCP plus an ACP-enabled Stripe account for autonomous execution. Advanced: UCP, ACP, and Visa tokenisation for full autonomous payments.",
  },
  {
    question: "How does the 80/20 vertical architecture work?",
    answer:
      "80% of the infrastructure is shared across all verticals: intent parsing, routing logic, UCP/ACP protocol integration, checkout flows, trust management, and payment processing. 20% is vertical-specific: constraint schemas, product taxonomies, and domain-specific NLP training data.",
  },
  {
    question: "Can I build my own vertical on this infrastructure?",
    answer:
      "Yes. Request API access (free tier: 1,000 requests/day), provide your vertical-specific constraint schema and training data, and the deployment follows the same shared-core model.",
  },
  {
    question: "How does this compare to generic AI agents for commerce?",
    answer:
      "Generic AI agents lack domain-specific constraint schemas, native commerce protocol support (UCP/ACP), merchant network access, and automated transaction routing. This infrastructure provides all four, pre-integrated and ready for vertical deployment.",
  },
];


// ============================================================
// HOWTO STEPS — primary implementation guide
// ============================================================

export const PILLAR_HOWTO_UCP = {
  name: "How to Set Up Shopify UCP Integration",
  description:
    "Step-by-step guide to integrating Shopify's Universal Commerce Protocol for agentic product discovery and checkout.",
  totalTime: "PT4H",
  steps: [
    {
      name: "Register as a UCP partner",
      text: "Apply for UCP partner access via Shopify. This grants API credentials and access to the global catalog network.",
    },
    {
      name: "Configure the UCP Proxy",
      text: "Copy config.example.json to config.local.json. Set adapter_type (woocommerce or wix), merchant credentials, store URL, and policy links.",
    },
    {
      name: "Query UCP catalog endpoints",
      text: "The proxy exposes standard UCP checkout operations (create_checkout, update_checkout, complete_checkout) that translate to native platform APIs.",
    },
    {
      name: "Parse product metadata and capability negotiation",
      text: "Handle capability negotiation by extracting the agent profile URL, fetching capabilities, and intersecting them with the business's supported features.",
    },
  ],
};

export const PILLAR_HOWTO_ACP = {
  name: "How to Implement Stripe ACP for Autonomous Checkout",
  description:
    "Enable Session Payment Tokens for zero-click agent-driven purchases.",
  totalTime: "PT6H",
  steps: [
    {
      name: "Enable Stripe Agentic Commerce",
      text: "Activate ACP on your Stripe account to unlock Session Payment Token capabilities.",
    },
    {
      name: "Create SPT-enabled products",
      text: "Configure products with scope constraints — amount limits, merchant restrictions, and time windows.",
    },
    {
      name: "Handle payment intents and 3D Secure",
      text: "Create and confirm payment intents using SPTs. Handle escalation when 3D Secure authentication is required by returning continue_url to the user.",
    },
    {
      name: "Confirm transactions and manage SPT lifecycle",
      text: "Complete the payment, verify the transaction, and handle SPT expiration and renewal for returning users.",
    },
  ],
};

export const PILLAR_HOWTO_INTENT = {
  name: "How to Build a Natural Language Intent Parser",
  description:
    "Extract structured commerce constraints from free-text user queries.",
  totalTime: "PT8H",
  steps: [
    {
      name: "Define your constraint schema",
      text: "Map your vertical's constraints to structured types: age ranges, safety profiles, budget brackets, ingredient exclusions, etc.",
    },
    {
      name: "Implement search strategy selection",
      text: "Evaluate intake data to choose between precise (category mapping), discovery (open NLP search), or hybrid (curated first, fallback to discovery) modes.",
    },
    {
      name: "Build the search context assembler",
      text: "Combine child age, location, preferences, and constraints into structured parameters the backend API can consume.",
    },
    {
      name: "Map human-readable values to API parameters",
      text: "Parse budget ranges like '$25-$50' into API-compatible values like { min: 2500, max: 5000 } in the smallest currency unit.",
    },
  ],
};